import { Button } from '@clientbase/clientbase-library'
import { Box, Typography, IconButton } from '@mui/material'
import { styled, css } from '@mui/material'

import { theme } from 'styles/theme'

// ============================================
// GLOBAL KEYFRAME ANIMATIONS (CSS)
// ============================================

const globalStyles = `
  @keyframes cb-pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(0.95);
    }
  }

  @keyframes cb-glow {
    0%, 100% {
      box-shadow: 0 0 20px ${theme.palette.primary.main}40,
                  0 0 40px ${theme.palette.primary.main}20,
                  0 0 60px ${theme.palette.primary.main}10;
    }
    50% {
      box-shadow: 0 0 30px ${theme.palette.primary.main}60,
                  0 0 60px ${theme.palette.primary.main}30,
                  0 0 90px ${theme.palette.primary.main}15;
    }
  }

  @keyframes cb-float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @keyframes cb-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @keyframes cb-wave {
    0%, 60%, 100% {
      transform: initial;
    }
    30% {
      transform: translateY(-8px);
    }
  }

  @keyframes cb-rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes cb-fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes cb-gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes cb-orbFloat {
    0%, 100% {
      transform: translate(0, 0) scale(1);
      opacity: 0.3;
    }
    25% {
      transform: translate(10px, -10px) scale(1.1);
      opacity: 0.5;
    }
    50% {
      transform: translate(0, -20px) scale(1);
      opacity: 0.3;
    }
    75% {
      transform: translate(-10px, -10px) scale(0.9);
      opacity: 0.4;
    }
  }

  @keyframes cb-futuristicPulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
      box-shadow:
        0 0 12px ${theme.palette.primary.main}80,
        0 0 24px ${theme.palette.primary.main}40;
    }
    25% {
      transform: scale(1.3) translateY(-6px);
      opacity: 1;
      box-shadow:
        0 0 20px ${theme.palette.primary.main},
        0 0 40px ${theme.palette.primary.main}60,
        0 0 60px ${theme.palette.primary.main}30;
    }
    50% {
      transform: scale(1);
      opacity: 0.8;
      box-shadow:
        0 0 12px ${theme.palette.primary.main}80,
        0 0 24px ${theme.palette.primary.main}40;
    }
    75% {
      transform: scale(0.9);
      opacity: 0.6;
      box-shadow:
        0 0 8px ${theme.palette.primary.main}60,
        0 0 16px ${theme.palette.primary.main}20;
    }
  }

  @keyframes cb-textGlow {
    0%, 100% {
      text-shadow: 0 0 10px ${theme.palette.primary.main}40;
    }
    50% {
      text-shadow:
        0 0 20px ${theme.palette.primary.main}60,
        0 0 40px ${theme.palette.primary.main}30;
    }
  }
`

// Inject global styles
if (typeof document !== 'undefined') {
  const styleId = 'cb-chat-assistant-animations'
  if (!document.getElementById(styleId)) {
    const styleElement = document.createElement('style')
    styleElement.id = styleId
    styleElement.textContent = globalStyles
    document.head.appendChild(styleElement)
  }
}

// ============================================
// CONTAINER STYLES
// ============================================

export const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`

export const InputWrapper = styled(Box)`
  width: 100%;
  border-radius: 15.267px;
  background: rgba(220, 220, 220, 0.07);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 12px;
  box-shadow: 0px 1.27px 15.27px 0px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.4);

  padding: 16px 32px 16px 16px;
`

export const InputContainer = styled(Box)<{ stack?: boolean }>`
  position: relative;
  border-radius: 12px;
  display: flex;
  width: 100%;
  flex-direction: ${({ stack }) => (stack ? 'column' : 'row')};
  align-items: ${({ stack }) => (stack ? 'stretch' : 'center')};
  gap: ${({ stack }) => (stack ? '8px' : '12px')};
  background-color: #fff;
  padding: 12px 8px;
`

export const ChatInput = styled('textarea')<{
  paddedForButton?: boolean
  atMax?: boolean
}>`
  flex: 1 1 auto;
  width: 100%;
  display: block;
  background: white;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: Inter, Roboto, sans-serif;
  line-height: 20px;
  padding: 8px ${({ paddedForButton }) => (paddedForButton ? '56px' : '8px')}
    8px 8px;
  border-radius: 8px;
  box-sizing: border-box;
  resize: none;
  overflow-y: ${({ atMax }) => (atMax ? 'auto' : 'hidden')};
  max-height: 200px;
  min-height: 36px;

  &::placeholder {
    color: ${theme.palette.neutral[500]};
  }
`

export const SendButton = styled(Button)<{
  hasMessage: boolean
  overlay?: boolean
}>`
  min-width: auto;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${theme.palette.primary.main};
  position: ${({ overlay }) => (overlay ? 'absolute' : 'static')};
  right: ${({ overlay }) => (overlay ? '12px' : 'auto')};
  ${({ overlay }) => (overlay ? 'top: 50%; transform: translateY(-50%);' : '')}
  z-index: 1;
`

export const ButtonRow = styled(Box)`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

// ============================================
// DRAWER STYLES
// ============================================

export const DrawerHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.palette.neutral[700]}15;
  background: #ffffff;
`

export const DrawerContent = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`

export const DrawerFooter = styled(Box)`
  padding: 16px 24px;
  border-top: 1px solid ${theme.palette.neutral[700]}15;
  background: #ffffff;
`

export const HeaderActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`

// ============================================
// MESSAGES AREA
// ============================================

export const MessagesArea = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 0px;
  position: relative;

  /* Futuristic scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      180deg,
      ${theme.palette.primary.main}60 0%,
      ${theme.palette.secondary.main}60 100%
    );
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(
      180deg,
      ${theme.palette.primary.main} 0%,
      ${theme.palette.secondary.main} 100%
    );
  }
`

export const MessageActions = styled(Box)`
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-top: 4px;
`

export const MessageContainer = styled(Box)<{ isUser: boolean }>`
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  animation: cb-fadeInUp 0.3s ease-out;

  &:hover .message-actions {
    opacity: 1;
  }
`

export const UserMessage = styled(Box)`
  background: linear-gradient(
    135deg,
    ${theme.palette.primary.main}20 0%,
    ${theme.palette.secondary.main}20 100%
  );
  border: 1px solid ${theme.palette.primary.main}30;
  color: ${theme.palette.neutral[200]};
  padding: 12px 16px;
  border-radius: 16px 16px 4px 16px;
  max-width: 70%;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 14px;
  font-family: Inter, Roboto, sans-serif;
  line-height: 1.5;
  margin-left: auto;
  align-self: flex-end;
  box-shadow: 0 2px 12px ${theme.palette.primary.main}10;
`

export const AgentMessage = styled(Box)`
  background: transparent;
  color: ${theme.palette.neutral[200]};
  padding: 12px 0px;
  border-radius: 12px;
  max-width: 100%;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 14px;
  font-family: Inter, Roboto, sans-serif;
  line-height: 1.6;
  margin-right: auto;
  align-self: flex-start;

  a {
    color: ${theme.palette.primary.main};
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;
    text-shadow: 0 0 10px ${theme.palette.primary.main}40;

    &:hover {
      text-decoration: underline;
      text-shadow: 0 0 20px ${theme.palette.primary.main}60;
    }
  }

  strong {
    font-weight: 600;
    color: ${theme.palette.neutral[100]};
  }
`

export const ActionButton = styled(IconButton)`
  padding: 6px;
  color: ${theme.palette.neutral[450]};
  opacity: 0.7;
  transition: all 0.2s ease;
  border-radius: 8px;

  &:hover {
    background: ${theme.palette.primary.main}15;
    color: ${theme.palette.primary.main};
    opacity: 1;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`

export const LoadingContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`

export const BottomFade = styled(Box)<{ visible?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 60px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    ${theme.palette.primary.main}08 40%,
    #ffffff 100%
  );
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 150ms ease;
  pointer-events: none;
`

export const ScrollDownButton = styled(IconButton)<{ visible?: boolean }>`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%)
    translateY(${({ visible }) => (visible ? '0' : '8px')});
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid ${theme.palette.primary.main}30;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08),
    0 0 0 1px ${theme.palette.primary.main}15;
  z-index: 2;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition: all 200ms ease;
  color: ${theme.palette.primary.main};

  &:hover {
    background: ${theme.palette.primary.main}08;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1),
      0 0 0 1px ${theme.palette.primary.main}30;
    transform: translateX(-50%) translateY(-2px);
  }
`

// ============================================
// AI LOADING INDICATOR - CLEAN & SOFT
// ============================================

export const AILoaderContainer = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 0;
  animation: cb-fadeInUp 0.3s ease-out;
`

export const AIAvatarLoader = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${theme.palette.primary.main}12 0%,
    ${theme.palette.primary.main}06 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  border: 1px solid ${theme.palette.primary.main}20;
`

export const LoaderContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
`

export const DotsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  height: auto;
  padding: 0;
  background: transparent;
  border-radius: 0;
  border: none;
`

export const Dot = styled(Box)<{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.palette.primary.main};
  animation: cb-wave 1.4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}ms;
  opacity: 0.7;
`

export const LoaderText = styled(Typography)<{ isVisible: boolean }>`
  font-size: 13px;
  color: ${theme.palette.neutral[400]};
  font-style: normal;
  font-weight: 400;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-4px')});
  transition: opacity 300ms ease, transform 300ms ease;
  min-height: 18px;
  letter-spacing: 0.2px;
`

// ============================================
// AI AVATAR FOR MESSAGES
// ============================================

export const AIAvatar = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    ${theme.palette.primary.main}15 0%,
    ${theme.palette.secondary.main}15 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  border: 1px solid ${theme.palette.primary.main}20;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 0 20px ${theme.palette.primary.main}30;
  }
`

export const MessageWrapper = styled(Box)<{ isUser: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 85%;
  ${({ isUser }) => isUser && 'flex-direction: row-reverse; margin-left: auto;'}
`

// ============================================
// HEADER
// ============================================

export const AssistantHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const AssistantInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const AssistantStatus = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${theme.palette.neutral[400]};
`

export const StatusDot = styled(Box)<{ isActive?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ isActive }) =>
    isActive
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success[300]} 100%)`
      : theme.palette.neutral[500]};
  box-shadow: ${({ isActive }) =>
    isActive
      ? `0 0 10px ${theme.palette.primary.main}60, 0 0 20px ${theme.palette.primary.main}30`
      : 'none'};
  ${({ isActive }) =>
    isActive &&
    css`
      animation: cb-pulse 2s ease-in-out infinite;
    `}
`

// ============================================
// EMPTY STATE
// ============================================

export const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
  gap: 24px;
  position: relative;
  overflow: hidden;

  /* Subtle grid background */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        ${theme.palette.primary.main}08 1px,
        transparent 1px
      ),
      linear-gradient(
        90deg,
        ${theme.palette.primary.main}08 1px,
        transparent 1px
      );
    background-size: 40px 40px;
    mask-image: radial-gradient(circle at center, black 30%, transparent 70%);
    -webkit-mask-image: radial-gradient(
      circle at center,
      black 30%,
      transparent 70%
    );
    pointer-events: none;
  }
`

export const WelcomeHeader = styled(Box)<{ hasMessages?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ hasMessages }) => (hasMessages ? '24px 20px' : '40px 20px')};
  text-align: center;
  gap: ${({ hasMessages }) => (hasMessages ? '12px' : '24px')};
  position: relative;
  border-bottom: ${({ hasMessages }) =>
    hasMessages ? `1px solid ${theme.palette.neutral[700]}15` : 'none'};
  margin-bottom: ${({ hasMessages }) => (hasMessages ? '16px' : '0')};
`

export const EmptyStateIcon = styled(Box)`
  width: 100px;
  height: 100px;
  border-radius: 28px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: cb-float 4s ease-in-out infinite;
  border: 1px solid ${theme.palette.neutral[600]};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
`

export const EmptyStateTitle = styled(Typography)`
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(
    135deg,
    ${theme.palette.neutral[100]} 0%,
    ${theme.palette.primary.main} 50%,
    ${theme.palette.secondary.main} 100%
  );
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: cb-gradientShift 4s ease-in-out infinite;
  text-shadow: none;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      ${theme.palette.primary.main},
      ${theme.palette.secondary.main},
      transparent
    );
    border-radius: 2px;
  }
`

export const EmptyStateSubtitle = styled(Typography)`
  font-size: 14px;
  color: ${theme.palette.neutral[400]};
  max-width: 380px;
  line-height: 1.7;
  position: relative;
`

export const SuggestedQuestions = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  max-width: 450px;
  margin-top: 16px;

  /* Quebra em 2 colunas quando a altura da tela é pequena */
  @media (max-height: 700px) {
    grid-template-columns: 1fr 1fr;
    max-width: 600px;
  }
`

export const SuggestedQuestion = styled(Box)`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.03) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid ${theme.palette.primary.main}20;
  border-radius: 16px;
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.palette.neutral[300]};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '→';
    font-size: 16px;
    color: ${theme.palette.primary.main};
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${theme.palette.primary.main}15,
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover {
    background: linear-gradient(
      135deg,
      ${theme.palette.primary.main}15 0%,
      ${theme.palette.primary.main}05 100%
    );
    border-color: ${theme.palette.primary.main}50;
    box-shadow: 0 8px 32px ${theme.palette.primary.main}20,
      0 0 0 1px ${theme.palette.primary.main}30,
      inset 0 0 20px ${theme.palette.primary.main}05;
    transform: translateY(-3px) translateX(4px);
    color: ${theme.palette.neutral[100]};
    padding-left: 24px;

    &::before {
      opacity: 1;
      transform: translateX(0);
    }

    &::after {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) translateX(2px);
    box-shadow: 0 4px 16px ${theme.palette.primary.main}15,
      0 0 0 1px ${theme.palette.primary.main}40;
  }
`

// ============================================
// MODAL INPUT AREA
// ============================================

export const FooterWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .disclaimer {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:focus-within .disclaimer {
    opacity: 1;
  }
`

export const Disclaimer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;

  span {
    font-size: 11px;
    color: ${theme.palette.neutral[500]};
  }
`

export const ModalInputWrapper = styled(Box)`
  position: relative;
  background: linear-gradient(
    135deg,
    ${theme.palette.neutral[800]}60 0%,
    ${theme.palette.neutral[800]}30 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid ${theme.palette.neutral[700]};
  border-radius: 16px;
  padding: 4px;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: ${theme.palette.primary.main}50;
    box-shadow: 0 0 30px ${theme.palette.primary.main}15,
      inset 0 0 20px ${theme.palette.primary.main}05;
  }
`

export const ModalTextarea = styled('textarea')<{
  paddedForButton?: boolean
  atMax?: boolean
}>`
  flex: 1 1 auto;
  width: 100%;
  display: block;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: Inter, Roboto, sans-serif;
  line-height: 20px;
  padding: 12px 56px 12px 16px;
  border-radius: 12px;
  box-sizing: border-box;
  resize: none;
  overflow-y: ${({ atMax }) => (atMax ? 'auto' : 'hidden')};
  max-height: 200px;
  min-height: 44px;
  color: ${theme.palette.neutral[200]};
  transition: opacity 0.2s ease;

  &::placeholder {
    color: ${theme.palette.neutral[500]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.palette.neutral[600]};
    border-radius: 4px;
  }
`

export const SendIconButton = styled(IconButton)<{ hasMessage: boolean }>`
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ hasMessage }) =>
    hasMessage
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
      : theme.palette.neutral[700]};
  color: ${({ hasMessage }) =>
    hasMessage ? theme.palette.neutral[100] : theme.palette.neutral[500]};
  transition: all 0.3s ease;
  box-shadow: ${({ hasMessage }) =>
    hasMessage ? `0 4px 15px ${theme.palette.primary.main}40` : 'none'};

  &:hover {
    background: ${({ hasMessage }) =>
      hasMessage
        ? `linear-gradient(135deg, ${theme.palette.primary[300]} 0%, ${theme.palette.secondary[200]} 100%)`
        : theme.palette.neutral[600]};
    transform: ${({ hasMessage }) => (hasMessage ? 'scale(1.05)' : 'none')};
    box-shadow: ${({ hasMessage }) =>
      hasMessage ? `0 6px 25px ${theme.palette.primary.main}50` : 'none'};
  }

  &:disabled {
    background: ${theme.palette.neutral[700]};
    color: ${theme.palette.neutral[600]};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

// ============================================
// TYPING INDICATOR - ALTERNATIVE STYLE
// ============================================

// ============================================
// BACKGROUND EFFECTS
// ============================================
